import { isTheme, normalizeLanguage, type AppLanguage, type AppTheme } from './catalog';

export interface PreferencesState { language: AppLanguage; theme: AppTheme }
export interface PreferenceTransport {
  platformApi: string;
  getAccessToken: () => string | null;
  fetchImpl?: typeof fetch;
}
const KEY = 'architech.preferences.v1';
const COOKIE = 'arch_prefs';
const listeners = new Set<() => void>();
let transport: PreferenceTransport | null = null;
let owner: string | null = null;
let pending: Partial<PreferencesState> = {};
let revision = 0;
let inflight: Promise<void> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function stored(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function parse(raw: string | null): Partial<PreferencesState> {
  try {
    const value = JSON.parse(raw ?? 'null');
    return value && typeof value === 'object' ? {
      ...(isTheme(value.theme) ? { theme: value.theme } : {}),
      ...(normalizeLanguage(value.language) ? { language: normalizeLanguage(value.language)! } : {}),
    } : {};
  } catch { return {}; }
}
function readCookie(): Partial<PreferencesState> {
  try {
    const raw = document.cookie.split('; ').find(item => item.startsWith(`${COOKIE}=`))?.slice(COOKIE.length + 1);
    return parse(raw ? decodeURIComponent(raw) : null);
  } catch { return {}; }
}
function initial(): PreferencesState {
  const local = parse(stored(KEY));
  const cookie = readCookie();
  const legacyTheme = stored('app_theme_v2') ?? stored('cosmetics_theme_v1') ?? stored('mainLandingTheme') ?? stored('publicMenuTheme');
  return {
    theme: cookie.theme ?? local.theme ?? (isTheme(legacyTheme) ? legacyTheme : 'light'),
    language: cookie.language ?? local.language ?? normalizeLanguage(stored('i18nextLng') ?? stored('cosmetics_language_v1')) ?? 'al',
  };
}
let state = initial();

function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
    // Compatibility for i18next detectors and legacy code while SSO migrates.
    localStorage.setItem('i18nextLng', state.language);
    localStorage.setItem('app_theme_v2', state.theme);
  } catch { /* Preferences remain available in memory. */ }
  const host = location.hostname;
  const domain = host === 'biznesi.al' || host.endsWith('.biznesi.al') ? '; Domain=.biznesi.al' : '';
  try {
    document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(state))}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${location.protocol === 'https:' ? '; Secure' : ''}`;
  } catch { /* Cookie storage may be blocked. */ }
}
function apply(next: Partial<PreferencesState>) {
  if ((!next.theme || next.theme === state.theme) && (!next.language || next.language === state.language)) return;
  state = { ...state, ...next };
  persist();
  listeners.forEach(listener => listener());
}
function identity(): { userId: string; token: string } | null {
  const token = transport?.getAccessToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.sub === 'string' ? { userId: payload.sub, token } : null;
  } catch { return null; }
}

async function sync() {
  const active = identity();
  if (!active || !transport) { owner = null; pending = {}; return; }
  if (owner !== active.userId) { owner = active.userId; pending = {}; revision++; }
  const config = transport;
  const sentRevision = revision;
  const patch = { ...pending };
  const writing = Object.keys(patch).length > 0;
  try {
    const response = await (config.fetchImpl ?? fetch)(`${config.platformApi.replace(/\/+$/, '')}/me/ui-preferences`, {
      method: writing ? 'PATCH' : 'GET',
      credentials: 'omit',
      headers: { Authorization: `Bearer ${active.token}`, ...(writing ? { 'Content-Type': 'application/json' } : {}) },
      ...(writing ? { body: JSON.stringify(patch) } : {}),
    });
    if (!response.ok) return; // Keep local choices; retry on focus/tick, including rollout 503.
    const data = parse(JSON.stringify(await response.json()));
    if (identity()?.userId !== active.userId || transport !== config) return;
    if (revision === sentRevision) { pending = {}; apply(data); }
    else apply({ ...data, ...pending }); // An earlier response cannot overwrite a newer choice.
  } catch { /* Offline: keep local choices and the pending patch for a later retry. */ }
}
export function refreshPreferences(): Promise<void> {
  if (inflight) return inflight;
  inflight = sync().finally(() => { inflight = null; });
  return inflight;
}
export function configurePreferences(config: PreferenceTransport) {
  transport = config;
  owner = null;
  pending = {};
  void refreshPreferences();
}
function watch() {
  apply(readCookie());
  if (document.visibilityState !== 'hidden') void refreshPreferences();
}
export const preferences = {
  getSnapshot: () => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    if (listeners.size === 1) {
      window.addEventListener('focus', watch);
      window.addEventListener('storage', watch);
      document.addEventListener('visibilitychange', watch);
      timer = setInterval(watch, 30_000);
      watch();
      persist(); // Publish migrated legacy choices even before the user changes a control.
    }
    return () => {
      listeners.delete(listener);
      if (!listeners.size) {
        window.removeEventListener('focus', watch);
        window.removeEventListener('storage', watch);
        document.removeEventListener('visibilitychange', watch);
        if (timer) clearInterval(timer);
        timer = null;
      }
    };
  },
  setTheme(theme: AppTheme) { if (isTheme(theme)) update({ theme }); },
  setLanguage(language: string) { const value = normalizeLanguage(language); if (value) update({ language: value }); },
};
function update(patch: Partial<PreferencesState>) {
  if ((!patch.theme || patch.theme === state.theme) && (!patch.language || patch.language === state.language)) return;
  apply(patch);
  persist();
  const active = identity();
  if (active) {
    if (owner !== active.userId) { owner = active.userId; pending = {}; }
    pending = { ...pending, ...patch };
    revision++;
    void refreshPreferences().then(() => {
      if (Object.keys(pending).length) void refreshPreferences();
    });
  }
}
